import { useEffect, useState } from 'react';
import { Section14Card } from './Section14Card';
import {
  Icon,
  ICONS_ENUM,
  Panel,
  TablePagination,
  Typography
} from '@sanlam-glacier/app-common/components';
import styles from './section-14-cards.module.scss';
import { Section14UrgencyTab } from 'modules/my-practice/types/section-14/section-14.enums';
import { InformationIndicator } from '../../atoms/information-indicator/InformationIndicator';
import { StatusPill } from '../../atoms/status-pill/StatusPill';
import clsx from 'clsx';
import { Section14ClientRenewalModel } from 'bff';

type Section14CardGridProps = {
  clients: Section14ClientRenewalModel[];
  activeTab: Section14UrgencyTab;
  currentPage: number;
  setActiveTabCurrentPage: (page: number) => void;
};

export const Section14CardGrid = ({
  clients,
  activeTab,
  currentPage,
  setActiveTabCurrentPage
}: Section14CardGridProps) => {
  const testComponentName = 'section14CardGrid';

  const [pageSize, setPageSize] = useState(7);
  const [page, setPage] = useState(currentPage);
  const [openPanelStates, setOpenPanelStates] = useState<{ [key: string]: boolean }>({});

  const paginatedData = clients.slice(page * pageSize, page * pageSize + pageSize).reverse();
  const rows = buildCardGridRows(paginatedData);

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const handleToggle = (id: number) => {
    setOpenPanelStates((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className={styles.cardGridContainer} data-testid={`${testComponentName}`}>
      {rows.map((row, i) =>
        row.type === 'cards' ? (
          <div
            key={`${row.singleInvestmentClients[0]?.clientId}CardGrid`}
            className={styles.cardGrid}
          >
            {row.singleInvestmentClients.map((client) => (
              <Section14Card
                key={`${client.clientId}CardKeyInGrid`}
                row={client}
                invIndex={0}
                activeTab={activeTab}
              />
            ))}
          </div>
        ) : (
          <>
            {row.multipleInvestmentClient.investments.length > 0 && (
              <Panel
                data-testid={`${testComponentName}MultiInvestmentClient${row.multipleInvestmentClient.clientName}Panel`}
                onChange={() => {
                  handleToggle(row.multipleInvestmentClient.clientId);
                }}
                className={
                  openPanelStates[row.multipleInvestmentClient.clientId]
                    ? clsx(styles.panelWrapper, styles.panelWrapperOpened)
                    : styles.panelWrapper
                }
                key={`${row.multipleInvestmentClient.clientId}Panel`}
                heading={{
                  children: (
                    <div className={styles.panelHeading}>
                      <div className={styles.panelHeadingNameContainer}>
                        <Typography
                          data-testid={`${testComponentName}MultiInvestmentClientName`}
                          color={Typography.COLOR.PRIMARY}
                          weight={Typography.WEIGHT.MEDIUM}
                          variant={Typography.VARIANT.B4}
                        >
                          {row.multipleInvestmentClient.clientName}
                        </Typography>
                        <Typography
                          color={Typography.COLOR.DEFAULT}
                          weight={Typography.WEIGHT.MEDIUM}
                          variant={Typography.VARIANT.B4}
                        >
                          {`Multiple investments`}
                        </Typography>
                      </div>
                      <InformationIndicator
                        activeTab={activeTab}
                        lateRenewalsInfo={row.multipleInvestmentClient.numLateRenewals}
                        upcomingRenewalsInfo={row.multipleInvestmentClient.numUpcomingRenewals}
                      />
                    </div>
                  )
                }}
                pill2={{
                  children: (
                    <StatusPill
                      daysUntilDue={row.multipleInvestmentClient.investments[0]?.daysUntilDue}
                      status={row.multipleInvestmentClient.investments[0]?.renewalStatus ?? ''}
                    />
                  ),
                  className: clsx(styles.cardGridStatusPill)
                }}
                button={{
                  children: (
                    <Icon icon={ICONS_ENUM.ACTION_VISIBILITY} className={styles.renewalFormIcon} />
                  ),
                  className: clsx(styles.panelHeadingButton),
                  disabled: true
                }}
                rightIconColor={'#A0A8B2'}
                rightIcon={ICONS_ENUM.FILE_DOWNLOAD}
                rightIconClick={() => {}}
              >
                <div className={styles.cardGrid}>
                  {row.multipleInvestmentClient.investments.map((inv, invIndex) => (
                    <Section14Card
                      key={`${inv.investmentNumber}KeyInCardGrid`}
                      row={row.multipleInvestmentClient}
                      invIndex={invIndex}
                      activeTab={activeTab}
                    />
                  ))}
                </div>
              </Panel>
            )}
          </>
        )
      )}

      <TablePagination
        count={clients.length}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(newPage: number) => {
          setActiveTabCurrentPage(newPage);
          setPage(newPage);
        }}
        onRowsPerPageChange={(newPageSize: number) => {
          setPage(0);
          setPageSize(newPageSize);
        }}
        rowsPerPageOptions={[3, 7, 9, 11]}
        closeMenuOnSelection
        showLabels={false}
        dropdownLabel='Clients per page:'
        className={styles.pagination}
      />
    </div>
  );
};

type CardGridRow =
  | { type: 'cards'; singleInvestmentClients: Section14ClientRenewalModel[] }
  | { type: 'accordion'; multipleInvestmentClient: Section14ClientRenewalModel };

const buildCardGridRows = (clients: Section14ClientRenewalModel[]): CardGridRow[] => {
  const rows: CardGridRow[] = [];

  let buffer: Section14ClientRenewalModel[] = [];

  for (const client of clients) {
    if (client.investments.length === 1) {
      buffer.push(client);

      if (buffer.length === 2) {
        rows.push({ type: 'cards', singleInvestmentClients: buffer });
        buffer = [];
      }
    } else {
      rows.push({ type: 'accordion', multipleInvestmentClient: client });
    }
  }

  if (buffer.length > 0) {
    rows.push({ type: 'cards', singleInvestmentClients: buffer });
  }

  return rows;
};
